import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

interface ClarificationCountdownProps {
  deadlineEpochMs?: number;
  /** The run's countdown is held (the user is typing an answer); hold the display too. */
  paused?: boolean;
  className?: string;
}

export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * How long the node keeps waiting for the answer. Anchored on the absolute
 * deadline the run announced, so it stays right across reloads and tab
 * throttling. Without a deadline the run waits indefinitely.
 */
export function ClarificationCountdown({
  deadlineEpochMs,
  paused = false,
  className,
}: ClarificationCountdownProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!deadlineEpochMs || paused) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [deadlineEpochMs, paused]);

  if (!deadlineEpochMs) {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        Waiting for your answer
      </span>
    );
  }

  const remaining = deadlineEpochMs - now;
  if (paused && remaining > 0) {
    return (
      <span
        className={cn('text-xs tabular-nums text-muted-foreground', className)}
      >
        {`Paused at ${formatRemaining(remaining)} while you type`}
      </span>
    );
  }
  if (remaining <= 0) {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        Time is up
      </span>
    );
  }
  return (
    <span
      className={cn('text-xs tabular-nums text-muted-foreground', className)}
      aria-live='off'
    >
      {`Answers in ${formatRemaining(remaining)}`}
    </span>
  );
}
